import { format } from 'date-fns';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import FlexBetween from 'components/FlexBetween';


const VALIDITY_TYPE_LABEL = {
  1: 'Days',
  2: 'Months ending at end',
  3: 'Months ending on day',
  4: 'Never Expires',
  5: 'Months ending at end +1 day',
  6: 'Calendar Year',
  7: 'Months ending on day -1 day',
};

const formatValidity = (subject) => {
  const type = subject.validityType ?? 2;

  if (!subject.validityPeriod) return '-';

  const typeLabel = VALIDITY_TYPE_LABEL[type] || '';
  return `${subject.validityPeriod} ${typeLabel}`;
};

export const STATUS_COLOR = {
  valid:           'success',
  'expiring-soon': 'warning',
  expired:         'error',
  missing:         'default',
};

const QualificationDetailDialog = ({ open, onClose, employee, subject, qual }) => {
  if (!employee || !subject) return null;
  const status = qual?.status || 'missing';
  return (
    <Dialog 
      fullWidth 
      maxWidth="xs" 
      open={open} 
      onClose={onClose}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <FlexBetween>
          <Typography variant="h6" fontWeight="bold">Qualification</Typography>
          <Chip
            label={status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
            color={STATUS_COLOR[status] || 'default'}
            size="small"
          />
        </FlexBetween>
        <Divider sx={{ mt: 1 }} />
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Box display="flex" flexDirection="column" gap={1}>
          <Box>
            <Typography variant="body1" fontWeight="bold">
              {employee.employeeCode} - {employee.employeeName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[employee.rank, employee.base].filter(Boolean).join(' · ') || '-'}
            </Typography>
          </Box>

          <>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="overline" color="text.secondary" lineHeight={1.5}>
              Subject
            </Typography>
            <Box >
              <Typography variant="body1" fontWeight="bold">
                {subject.subjectCode} - {subject.subjectName}
              </Typography>
              {subject.validityPeriod && (
                <Typography variant="body2" color="text.secondary">
                  Validity: {formatValidity(subject)}
                </Typography>
              )}
            </Box>
          </>

          {qual && qual.status !== 'missing' && (
            <>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="overline" color="text.secondary" lineHeight={1.5}>
                Due Date
              </Typography>
              <Box display="flex" flexDirection="column" gap={0.5}>
                {qual.trainingDate && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Last done
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {format(new Date(qual.trainingDate), 'dd/MM/yyyy')}
                    </Typography>
                  </Box>
                )}
                {qual.expiryDate && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Expiry date</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {format(new Date(qual.expiryDate), 'dd/MM/yyyy')}
                    </Typography>
                  </Box>
                )}
                {qual.score != null && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Score</Typography>
                    <Typography variant="body2" fontWeight="medium">{qual.score}</Typography>
                  </Box>
                )}
              </Box>
            </>
          )}

          {(!qual || qual.status === 'missing') && (
            <>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                No training session record found for this employee and subject.
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" size="small" color="secondary" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default QualificationDetailDialog;
